import { useEffect } from 'react';
import { APPFOLIO_PUSH_STATUS } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { getLiveAttempt } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush/appfolioPushConstants';
import { useStore } from 'src/stores';
import { getPropertyApGlLabel, getRecordedPropertyApGlLabel, getRecordedSummaryApGlLabel, getSummaryApGlLabel } from './apGlAccounts';

/**
 * The Accounts Payable GL account a bill's properties post to — the one it *did* post to once it has
 * been pushed, and the one it *would* post to before that.
 *
 * Those are different questions, and the difference is the point. A property's configured account can
 * be re-pointed at any time; a payable already in AppFolio stays where it posted. Showing the live
 * mapping against a pushed bill quietly misstates the ledger, which is the kind of wrong that only
 * surfaces at reconciliation.
 *
 * Two company-wide reads for the configured side rather than one per property: a bill can cover several,
 * and `createAsync` cancels the previous call on the same action, so asking per property would leave
 * every answer but the last cancelled. Both are read once for the page and answer every bill opened
 * on it.
 *
 * `isEnabled` covers the GL codes flag as well as AppFolio, because the service only fills the account
 * in while that flag is on — read with it off, every property would look unconfigured when the truth
 * is that we were not told.
 */
export const useApGlAccounts = _ref => {
  var _getLiveAttempt;
  let isEnabled = _ref.isEnabled,
    pushStatus = _ref.pushStatus;
  const store = useStore();
  useEffect(() => {
    if (!isEnabled) return;

    // Two different actions, so they do not cancel one another.
    store.appfolioGlAccounts.listPropertiesAsync.run();
    store.appfolioGlAccounts.listGlAccountsAsync.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  /**
   * Only a succeeded push has accounts worth reading back. `failed` and `notPushed` posted nothing, so
   * the configured account is the true forward-looking answer for both; `pending` has not landed yet.
   *
   * `needsReconciliation` is deliberately left on the configured side too: it means the document's rows
   * name more than one AppFolio bill, so there is no single attempt whose accounts describe what
   * happened, and picking the newest would present one payable's accounts as the document's.
   */
  const recordedAttemptId = (pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) === APPFOLIO_PUSH_STATUS.succeeded ? (_getLiveAttempt = getLiveAttempt(pushStatus)) === null || _getLiveAttempt === void 0 ? void 0 : _getLiveAttempt.pushAttemptId : null;
  useEffect(() => {
    if (!isEnabled || !recordedAttemptId) return;
    if (store.appfolioGlAccounts.getRecordedAccounts(recordedAttemptId)) return;
    store.appfolioGlAccounts.readPushGlAccountsAsync.run(recordedAttemptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, recordedAttemptId]);
  const hasRead = isEnabled && store.appfolioGlAccounts.hasRead;
  const recorded = recordedAttemptId ? store.appfolioGlAccounts.getRecordedAccounts(recordedAttemptId) : null;
  const getAccount = externalId => store.appfolioGlAccounts.getGlAccountByExternalId(externalId);
  return {
    isEnabled,
    /** Whether the labels describe what was posted rather than what is configured. */
    isRecorded: !!recordedAttemptId,
    /** One property's account, for a line item. */
    getPropertyLabel: propertyLivableId => recordedAttemptId ? getRecordedPropertyApGlLabel({
      recorded,
      propertyLivableId,
      getAccount
    }) : getPropertyApGlLabel({
      account: store.appfolioGlAccounts.getApGlAccount(propertyLivableId),
      hasRead
    }),
    /** The bill's account, when every line it posted agrees. */
    getSummaryLabel: propertyLivableIds => recordedAttemptId ? getRecordedSummaryApGlLabel({
      recorded,
      getAccount
    }) : getSummaryApGlLabel({
      accounts: (propertyLivableIds || []).map(id => store.appfolioGlAccounts.getApGlAccount(id)),
      hasRead
    })
  };
};